/** FeedModerationPanel — MC/SuperAdmin live feed moderation: hide/restore posts, delete comments. */
import { useEffect, useState, useCallback } from "react";
import api, { formatErr, fileUrl } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { toast } from "sonner";
import { Camera, EyeOff, Eye, Trash2, X, Search } from "lucide-react";
import { VipBadge } from "./VipBadge";
import { formatTimestamp } from "../lib/time";
import { T } from "../lib/i18n";

const FILTERS = ["All", "Photos", "Text-only", "Hidden", "Visible"];

export default function FeedModerationPanel() {
  const { eventId } = useMotif();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [pending, setPending] = useState(null); // {action, post, cid?}
  const [reason, setReason] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/mc/feed");
      setPosts(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEventSocket(eventId, (msg) => {
    if (msg.type === "feed_new" || msg.type === "feed_update") refresh();
  });

  const act = async () => {
    if (!pending) return;
    const { action, post, cid } = pending;
    try {
      if (action === "hide") await api.post(`/mc/feed/${post.id}/hide`, { reason });
      if (action === "restore") await api.post(`/mc/feed/${post.id}/restore`, { reason });
      if (action === "delete-comment") await api.post(`/mc/feed/${post.id}/comments/${cid}/delete`, { reason });
      toast.success(action === "hide" ? "Post hidden everywhere" : action === "restore" ? "Post restored" : "Comment deleted");
      setPending(null);
      setReason("");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const filtered = posts.filter((p) => {
    if (filter === "Photos" && !p.image_url) return false;
    if (filter === "Text-only" && p.image_url) return false;
    if (filter === "Hidden" && !p.hidden) return false;
    if (filter === "Visible" && p.hidden) return false;
    if (search && !(p.author_name || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="card-elev p-4" data-testid="mc-feed-panel">
      <div className="flex items-center gap-2 mb-3">
        <Camera className="w-4 h-4 text-primary-motif" />
        <div className="font-display text-lg text-motif">{T("Live Feed Moderation")}</div>
        <span className="ml-auto text-[10px] text-motif-muted">{posts.filter((p) => p.hidden).length} hidden · {posts.length} total</span>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-1 flex-1 min-w-[160px]">
          <Search className="w-3.5 h-3.5 text-motif-muted" />
          <input className="input-motif text-xs flex-1" placeholder="Search by guest…"
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="mc-feed-search" />
        </div>
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className="text-[10px] px-2 py-1 rounded-full"
            style={{
              background: filter === f ? "var(--motif-primary)" : "var(--motif-accent)",
              color: filter === f ? "var(--motif-primary-fg)" : "var(--motif-text-muted)",
            }}
            data-testid={`mc-feed-filter-${f.toLowerCase().replace(" ", "-")}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
        {filtered.length === 0 && <div className="text-motif-muted text-sm italic text-center py-4">{T("No posts match.")}</div>}
        {filtered.map((p) => (
          <div key={p.id} className="p-3 rounded-xl bg-accent-motif" style={{ opacity: p.hidden ? 0.6 : 1 }} data-testid={`mc-feed-post-${p.id}`}>
            <div className="flex gap-3">
              {p.image_url && (
                <button onClick={() => setPreview(p)} className="flex-shrink-0" data-testid={`mc-feed-thumb-${p.id}`}>
                  <img src={fileUrl(p.image_url)} alt="" className="w-16 h-16 object-cover rounded-lg" loading="lazy" />
                </button>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <VipBadge show={!!p.author_vip} />
                  <span className="font-display text-sm text-motif">{p.author_name}</span>
                  {p.author_table && <span className="text-[10px] text-motif-muted">· {p.author_table}</span>}
                  <span className="text-[9px] text-motif-muted">· {formatTimestamp(p.created_at)}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full ml-auto" style={{
                    background: p.hidden ? "#7a1f1f" : "var(--motif-primary)",
                    color: "#fff",
                  }} data-testid={`mc-feed-state-${p.id}`}>{p.hidden ? "Hidden" : "Visible"}</span>
                </div>
                {p.caption && <div className="text-xs text-motif mt-0.5 break-words">{p.caption}</div>}
                <div className="text-[8px] text-motif-muted mt-0.5">id: {p.id}</div>
                {(p.comments || []).length > 0 && (
                  <div className="mt-1.5 space-y-1">
                    {p.comments.map((c) => (
                      <div key={c.id} className="flex items-start gap-1.5 text-[11px] text-motif-muted" data-testid={`mc-feed-comment-${c.id}`}>
                        <span className="flex-1 min-w-0 break-words"><span className="font-medium text-motif">{c.author}:</span> {c.text}</span>
                        <button onClick={() => { setPending({ action: "delete-comment", post: p, cid: c.id }); setReason(""); }}
                          className="btn-ghost text-[9px] px-1 flex-shrink-0" aria-label="Delete comment"
                          data-testid={`mc-feed-comment-delete-${c.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-1.5 mt-2">
                  {!p.hidden ? (
                    <button onClick={() => { setPending({ action: "hide", post: p }); setReason(""); }}
                      className="btn-ghost text-[10px] px-2 py-1 flex items-center gap-1" data-testid={`mc-feed-hide-${p.id}`}>
                      <EyeOff className="w-3 h-3" /> Hide
                    </button>
                  ) : (
                    <button onClick={() => { setPending({ action: "restore", post: p }); setReason(""); }}
                      className="btn-primary text-[10px] px-2 py-1 flex items-center gap-1" data-testid={`mc-feed-restore-${p.id}`}>
                      <Eye className="w-3 h-3" /> Restore
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Confirm modal with reason */}
      {pending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" data-testid="mc-feed-confirm">
          <div className="card-elev p-5 w-full max-w-sm">
            <div className="font-display text-lg text-motif mb-1">
              {pending.action === "hide" ? "Hide this post?" : pending.action === "restore" ? "Restore this post?" : "Delete this comment?"}
            </div>
            <p className="text-sm text-motif-muted mb-3 leading-snug">
              {pending.action === "hide" && "The post is removed immediately from the guest feed, MC Show, and the projector. It can be restored later."}
              {pending.action === "restore" && "The post becomes visible again on the guest feed, MC Show, and the projector."}
              {pending.action === "delete-comment" && "The comment is permanently removed from the post. This is recorded in the audit log."}
            </p>
            <input className="input-motif text-xs w-full mb-3" placeholder="Reason (recorded in audit log)"
              value={reason} onChange={(e) => setReason(e.target.value)} data-testid="mc-feed-reason" />
            <div className="flex gap-2">
              <button onClick={() => { setPending(null); setReason(""); }} className="btn-ghost text-sm flex-1" data-testid="mc-feed-confirm-cancel">{T("Cancel")}</button>
              <button onClick={act} className="btn-primary text-sm flex-1" data-testid="mc-feed-confirm-ok">{T("Confirm")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Image preview */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 px-4" onClick={() => setPreview(null)} data-testid="mc-feed-preview">
          <button className="absolute top-4 right-4 text-white" aria-label="Close preview" data-testid="mc-feed-preview-close">
            <X className="w-7 h-7" />
          </button>
          <img src={fileUrl(preview.image_url)} alt="" className="max-h-[85vh] max-w-full rounded-xl object-contain" />
        </div>
      )}
    </div>
  );
}
